import { Component, Event, EventEmitter, JSX, Method, Prop, Watch, h } from '@stencil/core'
import SlimSelect from 'slim-select'

import { NoneSelected, UnansweredValue } from '../air-question/air-question.types'
import { MultiSelectOption } from './air-dropdown-multi.types'
import { Validation } from '../../types/status.types'
import { isUndefined } from '../../utils/helpers'

@Component({
  tag: 'air-dropdown-multi',
  styleUrl: 'air-dropdown-multi.scss',
  shadow: true,
})
export class AirDropdownMulti {
  /** Defines options */
  @Prop() options!: MultiSelectOption[] | undefined
  @Watch('options')
  watchOptionsChange(options: MultiSelectOption[] | typeof UnansweredValue): void {
    const selectedOptions = options?.filter((option) => option.selected)
    const isNoneSelected = selectedOptions?.some((option) => option.key === NoneSelected)
    const noSelection = selectedOptions && selectedOptions.length === 0

    if (noSelection && this.answers === UnansweredValue) {
      this.initAnswers(UnansweredValue)
      return
    }

    if (isNoneSelected || (Array.isArray(this.answers) && this.answers.length === 0)) {
      this.slimSelect.setSelected([NoneSelected], false)
      this.initAnswers([])
      return
    }

    if (selectedOptions) {
      this.slimSelect.setSelected(
        selectedOptions.map(({ key }) => key.toString()),
        false,
      )
    }
    this.initAnswers(selectedOptions ?? UnansweredValue)
  }

  /** Specifies placeholder value */
  @Prop() placeholder?: string

  /** Defines if dropdown is disabled */
  @Prop() disabled = false

  /** Defines if dropdown is required */
  @Prop() required = false

  /** Defines if dropdown should have autofocus */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() autofocus = false

  /** Specifies validation of dropdown */
  @Prop() validation?: Validation

  private renderValidation(): JSX.Element | null {
    return this.validation?.text !== undefined ? (
      <span class="status-text">{this.validation.text}</span>
    ) : null
  }

  /** Emits value on change */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<MultiSelectOption['key'][] | null>

  /** Emits value on focus */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<MultiSelectOption['key'][] | null>

  /** Emits value on blur */
  @Event({ eventName: 'air-blur' }) blur!: EventEmitter<MultiSelectOption['key'][] | null>

  private slimSelect!: SlimSelect
  private slimSelectElement!: HTMLSelectElement
  private slimContentLocation!: HTMLDivElement

  /** Manually focus the select element */
  @Method()
  async setFocus(): Promise<void> {
    this.slimSelectElement && this.slimSelectElement.focus && this.slimSelectElement.focus()
  }

  private handleFocus = (): void => {
    if (this.disabled) return
    this.focus.emit(this.answers)
  }

  /** Manually blur the select element */
  @Method()
  async setBlur(): Promise<void> {
    this.slimSelectElement?.blur()
  }

  private answers: MultiSelectOption['key'][] | typeof UnansweredValue | undefined
  private initAnswers(options: MultiSelectOption[] | null): void {
    this.answers =
      options === UnansweredValue
        ? UnansweredValue
        : options.filter(({ selected }) => selected).map(({ key }) => key)
  }

  async componentDidLoad(): Promise<void> {
    this.autofocus && (await this.setFocus())

    this.initAnswers(
      this.options?.some((option) => option.selected) ? this.options : UnansweredValue,
    )

    const data = this.options?.map((option) => ({
      text: option.label,
      value: option.key.toString(),
      selected: option.selected ?? false,
    }))

    this.slimSelect = new SlimSelect({
      select: this.slimSelectElement,
      settings: {
        contentLocation: this.slimContentLocation,
        contentPosition: 'fixed',
        closeOnSelect: false,
        placeholderText: this.placeholder ?? 'Select',
        // Search conflicts with Stencil due to event retargetting and doesn't work
        showSearch: false,
      },
      events: {
        beforeOpen: (): void => {
          this.handleFocus()
        },
        // In afterChange, the selected option is still marked as selected=false
        // within "updatedOptions". So if something is marked as selected=true,
        // it means it was already selected previously.
        afterChange: (updatedOptions): void => {
          const previousAnswers = this.answers ? [...this.answers] : UnansweredValue
          const noneClicked =
            updatedOptions.find((option) => option.value === NoneSelected && !option.selected) !==
              undefined &&
            (previousAnswers === UnansweredValue || previousAnswers.length > 0)

          if (noneClicked) {
            this.initAnswers([])
            this.change?.emit(this.answers)
            return
          }

          const updatedAnswers = updatedOptions
            .map(
              (updatedOption) =>
                this.options?.find((option) => option.key.toString() === updatedOption.value)?.key,
            )
            .filter((key) => !isUndefined(key) && key !== NoneSelected)

          this.answers = updatedAnswers.length === 0 ? UnansweredValue : updatedAnswers
          this.change?.emit(this.answers)
        },
        afterClose: (): void => {
          this.blur?.emit(this.answers)
        },
      },
      data,
    })
  }

  render(): JSX.Element {
    const wrapperClasses = {
      'air-dropdown-multi-wrapper': true,
      [`with-status`]: this.validation?.status !== undefined,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }
    return (
      <div class={wrapperClasses}>
        <select
          multiple
          ref={(el: HTMLSelectElement | undefined): void => {
            if (el) this.slimSelectElement = el
          }}
        ></select>
        <div
          ref={(el: HTMLDivElement | undefined): void => {
            if (el) this.slimContentLocation = el
          }}
        ></div>
        {this.renderValidation()}
      </div>
    )
  }
}
